import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaSearch, FaArrowRight, FaTimes } from "react-icons/fa";

const formatDisplay = (d) => {
  if (!d) return "Select date";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function CheckAvailability({
  fromDate,
  toDate,
  onDateChange,
  onCheck,
}) {
  const [internalFrom, setInternalFrom] = useState(fromDate || new Date());
  const [internalTo, setInternalTo] = useState(
    toDate || new Date(new Date().setDate(new Date().getDate() + 10)),
  );
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Sync if parent dates change (e.g. from URL params)
  useEffect(() => {
    if (fromDate) setInternalFrom(fromDate);
    if (toDate) setInternalTo(toDate);
  }, [fromDate, toDate]);

  // ✅ FIX: use internalFrom/internalTo, not fromDate/toDate
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    if (start) setInternalFrom(start);
    if (end) {
      setInternalTo(end);
      onDateChange?.(start, end);
    } else {
      setInternalTo(null);
    }
  };

  const handleReset = () => {
    const from = new Date();
    const to = new Date(new Date().setDate(new Date().getDate() + 10));
    setInternalFrom(from);
    setInternalTo(to);
    onDateChange?.(from, to);
  };

  const handleDone = () => {
    if (internalFrom && internalTo) {
      onDateChange?.(internalFrom, internalTo);
    }
    setOpen(false);
  };

  const daysDiff =
    internalFrom && internalTo
      ? Math.round((internalTo - internalFrom) / (1000 * 60 * 60 * 24))
      : 0;

  return (
    <>
      <style>{`
        .dp-dark .react-datepicker {
          background: transparent !important;
          border: none !important;
          font-family: inherit !important;
          color: white !important;
        }
        .dp-dark .react-datepicker__month-container { padding: 0 8px; }
        .dp-dark .react-datepicker__header {
          background: transparent !important;
          border-bottom: 1px solid #334155 !important;
          padding-bottom: 12px !important;
        }
        .dp-dark .react-datepicker__current-month {
          color: #f1f5f9 !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          margin-bottom: 10px !important;
        }
        .dp-dark .react-datepicker__day-name {
          color: #64748b !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          width: 34px !important;
          text-transform: uppercase;
        }
        .dp-dark .react-datepicker__day {
          color: #cbd5e1 !important;
          width: 34px !important;
          line-height: 34px !important;
          border-radius: 50% !important;
          font-size: 13px !important;
          margin: 2px !important;
          transition: all 0.15s !important;
        }
        .dp-dark .react-datepicker__day:hover {
          background: #0d948844 !important;
          color: #fff !important;
        }
        .dp-dark .react-datepicker__day--selected,
        .dp-dark .react-datepicker__day--range-start,
        .dp-dark .react-datepicker__day--range-end {
          background: #0d9488 !important;
          color: #fff !important;
          font-weight: 700 !important;
        }
        .dp-dark .react-datepicker__day--in-range {
          background: #0d948833 !important;
          color: #e2e8f0 !important;
          border-radius: 0 !important;
        }
        .dp-dark .react-datepicker__day--range-start { border-radius: 50% 0 0 50% !important; }
        .dp-dark .react-datepicker__day--range-end { border-radius: 0 50% 50% 0 !important; }
        .dp-dark .react-datepicker__day--disabled { color: #2d3f55 !important; cursor: not-allowed !important; }
        .dp-dark .react-datepicker__day--outside-month { color: #2d3f55 !important; }
        .dp-dark .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
        .dp-dark .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: #fff !important; }
        .dp-dark .react-datepicker__navigation { top: 8px !important; }
        .dp-dark .react-datepicker__day--in-selecting-range {
          background: #0d948833 !important;
          border-radius: 0 !important;
        }
        .dp-dark .react-datepicker__day--selecting-range-start { border-radius: 50% 0 0 50% !important; }
        .dp-dark .react-datepicker__day--today {
          font-weight: 700 !important;
          text-decoration: underline !important;
          text-underline-offset: 3px !important;
        }

        /* Mobile: stack months vertically */
        @media (max-width: 639px) {
          .dp-dark .react-datepicker {
            display: flex !important;
            flex-direction: column !important;
          }
          .dp-dark .react-datepicker__month-container {
            padding: 0 0 16px 0 !important;
            width: 100% !important;
          }
          .dp-dark .react-datepicker__day-name,
          .dp-dark .react-datepicker__day {
            width: 38px !important;
            line-height: 38px !important;
          }
        }
      `}</style>

      {/* ── Availability Card ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-3">
        {/* Header row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="bg-teal-50 p-1.5 rounded-lg">
              <FaCalendarAlt className="text-teal-600 text-sm" />
            </div>
            <h3 className="text-slate-700 font-bold text-sm tracking-wide">
              Check Availability
            </h3>
          </div>
          <span className="text-[10px] text-slate-400 font-medium uppercase tracking-widest hidden sm:block">
            Select date range
          </span>
        </div>

        {/* Divider */}
        <div className="h-px bg-slate-100 mb-2" />

        {/* Controls row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Date Range Block */}
          <div
            onClick={() => setOpen(true)}
            className="flex items-center flex-1 cursor-pointer group bg-slate-50 hover:bg-teal-50/40 border border-slate-200 hover:border-teal-400 rounded-xl overflow-hidden transition-all duration-200 hover:shadow-[0_0_0_3px_#0d948815]"
          >
            {/* From */}
            <div className="flex items-center gap-3 px-4 py-3 flex-1">
              <div className="bg-white border border-slate-200 group-hover:border-teal-300 p-2 rounded-lg transition-colors shadow-sm">
                <FaCalendarAlt className="text-teal-500 text-xs" />
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-0.5">
                  From
                </p>
                <p className="text-sm font-bold text-slate-800 leading-none">
                  {formatDisplay(internalFrom)}
                </p>
              </div>
            </div>

            {/* Divider with arrow */}
            <div className="flex flex-col items-center px-2">
              <div className="h-6 w-px bg-slate-200" />
              <FaArrowRight className="text-slate-300 text-[10px] my-1" />
              <div className="h-6 w-px bg-slate-200" />
            </div>

            {/* To */}
            <div className="flex items-center gap-3 px-4 py-3 flex-1">
              <div className="bg-white border border-slate-200 group-hover:border-teal-300 p-2 rounded-lg transition-colors shadow-sm">
                <FaCalendarAlt className="text-teal-500 text-xs" />
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-0.5">
                  To
                </p>
                <p className="text-sm font-bold text-slate-800 leading-none">
                  {formatDisplay(internalTo)}
                </p>
              </div>
            </div>

            {/* Edit hint */}
            <div className="pr-4 hidden sm:flex items-center">
              <span className="text-[10px] text-teal-500 font-semibold bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                Edit ✎
              </span>
            </div>
          </div>

          {/* Check Availability Button */}
          <button
            onClick={() => onCheck?.()}
            className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white font-bold text-sm px-6 py-3.5 rounded-xl transition-all duration-200 shadow-md shadow-teal-600/25 whitespace-nowrap sm:w-auto w-full"
          >
            <FaSearch className="text-xs" />
            Check Availability
          </button>
        </div>

        {/* Bottom hint */}
        <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-400" />
          Click the date range to modify your selection
        </p>
      </div>

      {/* ── Modal Overlay ── */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 w-full sm:max-w-[700px] mx-0 sm:mx-4 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-teal-600 text-white rounded-lg p-2">
                  <FaCalendarAlt className="text-sm" />
                </div>
                <div>
                  <p className="text-white font-bold text-base leading-tight">
                    Select Date Range
                  </p>
                  <p className="text-slate-400 text-xs">
                    Choose your from and to dates
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReset}
                  className="text-slate-400 hover:text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-all"
                >
                  Reset
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg p-1.5 transition-all"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            {/* Selected Range Bar */}
            <div className="flex items-center gap-2 mb-4 bg-slate-800 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                  From
                </span>
                <span className="text-sm font-semibold text-white">
                  {formatDisplay(internalFrom)}
                </span>
              </div>
              <FaArrowRight className="text-slate-600 text-xs mx-auto" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">
                  {internalTo ? (
                    formatDisplay(internalTo)
                  ) : (
                    <span className="text-slate-500 text-xs">
                      Pick end date
                    </span>
                  )}
                </span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                  To
                </span>
                <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
              </div>
            </div>

            {/* ✅ FIX: use internalFrom / internalTo here */}
            <div className="dp-dark flex justify-center overflow-x-auto">
              <DatePicker
                selected={internalFrom}
                onChange={handleDateChange}
                startDate={internalFrom}
                endDate={internalTo}
                selectsRange
                inline
                monthsShown={isMobile ? 1 : 2}
                minDate={new Date()}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
              <p className="text-slate-500 text-xs">
                {daysDiff > 0 ? (
                  <>
                    <span className="text-teal-400 font-semibold">
                      {daysDiff}
                    </span>{" "}
                    days selected
                  </>
                ) : (
                  "Select a date range"
                )}
              </p>
              <button
                onClick={handleDone}
                disabled={!internalFrom || !internalTo}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-white font-semibold text-sm px-6 py-2 rounded-xl transition-all duration-200 shadow-lg shadow-teal-600/30"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
